import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Progress,
  Tag,
  Spin,
  Empty,
  Table,
  message,
} from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  DollarOutlined,
  RiseOutlined,
  ExperimentOutlined,
  CrownOutlined,
  FallOutlined,
  UserAddOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { dashboardApi } from '../../services/admin';
import { PLAN_COLORS, FUNNEL_COLORS, COLORS } from '../../constants/colors';
import { SPACING, RADIUS, FONT_SIZE, FONT_WEIGHT, SIZE } from '../../constants/design';
import type {
  DashboardMetrics,
  SubscriptionDistribution,
  ConversionStage,
  ChurnRiskTenant,
  MonthlyRevenue,
  MonthlyGrowth,
} from '../../types/admin';

const { Title, Text } = Typography;

// 版本颜色映射（已迁移至 constants/colors.ts）

// 版本中文名映射
const planNames: Record<string, string> = {
  Free: '免费版',
  Starter: '入门版',
  Professional: '专业版',
  Enterprise: '企业版',
};

export const AdminDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [distributions, setDistributions] = useState<SubscriptionDistribution[]>([]);
  const [stages, setStages] = useState<ConversionStage[]>([]);
  const [churnRiskData, setChurnRiskData] = useState<ChurnRiskTenant[]>([]);
  const [revenueTrend, setRevenueTrend] = useState<MonthlyRevenue[]>([]);
  const [growthTrend, setGrowthTrend] = useState<MonthlyGrowth[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [metricsRes, distRes, funnelRes, churnRes, revenueRes, growthRes] = await Promise.all([
        dashboardApi.getDashboardMetrics(),
        dashboardApi.getSubscriptionDistribution(),
        dashboardApi.getTrialConversionFunnel(),
        dashboardApi.getChurnRiskTenants(10),
        dashboardApi.getRevenueTrend(6),
        dashboardApi.getGrowthTrend(6),
      ]);

      if (metricsRes.data) {
        setMetrics(metricsRes.data);
      }
      if (distRes.data) {
        setDistributions(distRes.data.distributions || []);
      }
      if (funnelRes.data) {
        setStages(funnelRes.data.stages || []);
      }
      if (churnRes.data) {
        setChurnRiskData(Array.isArray(churnRes.data) ? churnRes.data : []);
      }
      if (revenueRes.data) {
        setRevenueTrend(Array.isArray(revenueRes.data) ? revenueRes.data : []);
      }
      if (growthRes.data) {
        setGrowthTrend(Array.isArray(growthRes.data) ? growthRes.data : []);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      message.error('获取仪表盘数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 格式化金额
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // 格式化百分比
  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  // 获取流失率颜色
  const getChurnRateColor = (rate: number) => {
    if (rate > 0.05) return COLORS.error;
    if (rate > 0.03) return COLORS.warning;
    return COLORS.success;
  };

  // 渲染漏斗阶段
  const renderFunnelStage = (stage: ConversionStage, index: number, totalStages: number) => {
    const widthPercent = 100 - (index * (60 / totalStages));
    const colors = FUNNEL_COLORS;
    const bgColor = colors[index % colors.length];

    return (
      <div
        key={stage.name}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: SPACING.md,
        }}
      >
        <div
          style={{
            width: `${widthPercent}%`,
            minWidth: SIZE.selectMd,
            background: `linear-gradient(90deg, ${bgColor} 0%, ${bgColor}dd 100%)`,
            borderRadius: RADIUS.lg,
            padding: `${SPACING.md}px ${SPACING.base}px`,
            color: '#fff',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          <div style={{ fontWeight: FONT_WEIGHT.semibold, fontSize: FONT_SIZE.md }}>{stage.name}</div>
          <div style={{ fontSize: FONT_SIZE.lg, fontWeight: FONT_WEIGHT.bold, marginTop: SPACING.xs }}>
            {stage.count.toLocaleString()}
          </div>
          {stage.rate > 0 && (
            <Tag
              color="default"
              style={{
                position: 'absolute',
                right: -40,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'var(--color-white)',
                color: COLORS.success,
                borderColor: COLORS.success,
              }}
            >
              {formatPercent(stage.rate)}
            </Tag>
          )}
        </div>
        {index < totalStages - 1 && (
          <div
            style={{
              width: 2,
              height: 16,
              background: 'var(--color-border-light)',
              margin: `${SPACING.xs}px 0`,
            }}
          />
        )}
      </div>
    );
  };

  return (
    <Spin spinning={loading}>
      <div>
        <Title level={4} style={{ marginBottom: SPACING.xl }}>
          运营仪表盘
        </Title>

        {/* 第一行核心指标 */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总租户数"
                value={metrics?.total_tenants || 0}
                prefix={<TeamOutlined style={{ color: COLORS.primary, marginRight: 8 }} />}
                valueStyle={{ color: COLORS.primary }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="活跃租户数"
                value={metrics?.active_tenants || 0}
                prefix={<UserOutlined style={{ color: COLORS.success, marginRight: 8 }} />}
                valueStyle={{ color: COLORS.success }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="MRR 月收入"
                value={formatCurrency(metrics?.mrr || 0)}
                prefix={<DollarOutlined style={{ color: COLORS.purple, marginRight: 8 }} />}
                valueStyle={{ color: COLORS.purple }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="试用转化率"
                value={formatPercent(metrics?.trial_conversion_rate || 0)}
                prefix={<RiseOutlined style={{ color: COLORS.orange, marginRight: 8 }} />}
                valueStyle={{ color: COLORS.orange }}
              />
            </Card>
          </Col>
        </Row>

        {/* 第二行指标 */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="试用中租户"
                value={metrics?.trial_tenants || 0}
                prefix={<ExperimentOutlined style={{ color: COLORS.cyan, marginRight: 8 }} />}
                valueStyle={{ color: COLORS.cyan }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="付费租户数"
                value={metrics?.paid_tenants || 0}
                prefix={<CrownOutlined style={{ color: COLORS.warning, marginRight: 8 }} />}
                valueStyle={{ color: COLORS.warning }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="ARR 年收入"
                value={formatCurrency(metrics?.arr || 0)}
                prefix={<DollarOutlined style={{ color: COLORS.magenta, marginRight: 8 }} />}
                valueStyle={{ color: COLORS.magenta }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="流失率"
                value={formatPercent(metrics?.churn_rate || 0)}
                prefix={<FallOutlined style={{ color: getChurnRateColor(metrics?.churn_rate || 0), marginRight: 8 }} />}
                valueStyle={{ color: getChurnRateColor(metrics?.churn_rate || 0) }}
              />
            </Card>
          </Col>
        </Row>

        {/* ARPU 和 新增租户指标 */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Card>
              <Statistic
                title="近30天新增租户"
                value={metrics?.new_tenants_30d || 0}
                prefix={<UserAddOutlined style={{ color: COLORS.success, marginRight: 8 }} />}
                valueStyle={{ color: COLORS.success, fontSize: 32 }}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card>
              <Statistic
                title="ARPU (平均每用户收入)"
                value={formatCurrency(metrics?.arpu || 0)}
                prefix={<DollarOutlined style={{ color: COLORS.purple, marginRight: 8 }} />}
                valueStyle={{ color: COLORS.purple, fontSize: 32 }}
              />
            </Card>
          </Col>
        </Row>

        {/* 版本分布和转化漏斗 */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Card title="订阅版本分布" style={{ height: '100%' }}>
              {distributions.length === 0 ? (
                <Empty description="暂无数据" />
              ) : (
                <div>
                  {distributions.map((item) => (
                    <div key={item.plan_name} style={{ marginBottom: SPACING.lg }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACING.sm }}>
                        <div>
                          <Tag
                            color={PLAN_COLORS[item.plan_name] || 'default'}
                            style={{ marginRight: SPACING.sm }}
                          >
                            {planNames[item.plan_name] || item.plan_name}
                          </Tag>
                          <Text type="secondary">{item.count} 租户</Text>
                        </div>
                        <Text strong>{formatCurrency(item.revenue)}/月</Text>
                      </div>
                      <Progress
                        percent={Math.round(item.percentage * 100)}
                        strokeColor={PLAN_COLORS[item.plan_name] || COLORS.primary}
                        format={(percent) => `${percent}%`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </Col>
          <Col span={12}>
            <Card title="试用转化漏斗" style={{ height: '100%' }}>
              {stages.length === 0 ? (
                <Empty description="暂无数据" />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: `${SPACING.lg}px ${SPACING['3xl']}px`,
                  }}
                >
                  {stages.map((stage, index) =>
                    renderFunnelStage(stage, index, stages.length)
                  )}
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* 流失风险预警 */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Card
              title={
                <span>
                  <WarningOutlined style={{ color: COLORS.warning, marginRight: SPACING.sm }} />
                  流失风险预警
                </span>
              }
              extra={
                <Tag color="red">
                  高风险: {churnRiskData.filter(t => t.risk_level === 'high').length}个
                </Tag>
              }
            >
              {churnRiskData.length === 0 ? (
                <Empty description="暂无高风险租户" />
              ) : (
                <Table
                  columns={[
                    { title: '租户', dataIndex: 'tenant_name', key: 'tenant_name' },
                    {
                      title: '风险等级',
                      dataIndex: 'risk_level',
                      key: 'risk_level',
                      render: (level: string) => {
                        const colorMap: Record<string, string> = { high: 'red', medium: 'orange', low: 'green' };
                        const labelMap: Record<string, string> = { high: '高', medium: '中', low: '低' };
                        return <Tag color={colorMap[level] || 'default'}>{labelMap[level] || level}</Tag>;
                      },
                    },
                    {
                      title: '健康度',
                      dataIndex: 'health_score',
                      key: 'health_score',
                      render: (score: number) => (
                        <Progress
                          percent={score}
                          size="small"
                          strokeColor={score < 50 ? COLORS.error : score < 70 ? COLORS.warning : COLORS.success}
                        />
                      ),
                    },
                    { title: '未活跃天数', dataIndex: 'days_since_active', key: 'days_since_active' },
                    {
                      title: '风险因素',
                      dataIndex: 'risk_factors',
                      key: 'risk_factors',
                      render: (factors: string[]) => (
                        <span>
                          {(factors || []).map((f, idx) => (
                            <Tag key={idx} style={{ marginBottom: SPACING.xs }}>{f}</Tag>
                          ))}
                        </span>
                      ),
                    },
                  ]}
                  dataSource={churnRiskData}
                  rowKey="tenant_id"
                  size="small"
                  pagination={false}
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* 趋势图表 */}
        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Card title="收入趋势（近6月）">
              {revenueTrend.length === 0 ? (
                <Empty description="暂无数据" />
              ) : (
                <Table
                  columns={[
                    { title: '月份', dataIndex: 'month', key: 'month' },
                    {
                      title: 'MRR',
                      dataIndex: 'mrr',
                      key: 'mrr',
                      render: (v: number) => formatCurrency(v),
                    },
                    {
                      title: '新增收入',
                      dataIndex: 'new_revenue',
                      key: 'new_revenue',
                      render: (v: number) => (
                        <span style={{ color: COLORS.success }}>+{formatCurrency(v)}</span>
                      ),
                    },
                    {
                      title: '流失收入',
                      dataIndex: 'churn_revenue',
                      key: 'churn_revenue',
                      render: (v: number) => (
                        <span style={{ color: COLORS.error }}>-{formatCurrency(v)}</span>
                      ),
                    },
                    {
                      title: '环比',
                      key: 'growth_rate',
                      render: (_: unknown, __: unknown, index: number) => {
                        if (index === 0) return '-';
                        const prev = revenueTrend[index - 1].mrr;
                        const curr = revenueTrend[index].mrr;
                        if (prev === 0) return '-';
                        const rate = ((curr - prev) / prev * 100).toFixed(1);
                        const isPositive = curr >= prev;
                        return (
                          <span style={{ color: isPositive ? COLORS.success : COLORS.error }}>
                            {isPositive ? '+' : ''}{rate}%
                          </span>
                        );
                      },
                    },
                  ]}
                  dataSource={revenueTrend}
                  rowKey="month"
                  size="small"
                  pagination={false}
                />
              )}
            </Card>
          </Col>
          <Col span={12}>
            <Card title="租户增长趋势（近6月）">
              {growthTrend.length === 0 ? (
                <Empty description="暂无数据" />
              ) : (
                <Table
                  columns={[
                    { title: '月份', dataIndex: 'month', key: 'month' },
                    {
                      title: '新增',
                      dataIndex: 'new_count',
                      key: 'new_count',
                      render: (v: number) => <span style={{ color: COLORS.success }}>+{v}</span>,
                    },
                    {
                      title: '流失',
                      dataIndex: 'churn_count',
                      key: 'churn_count',
                      render: (v: number) => <span style={{ color: COLORS.error }}>-{v}</span>,
                    },
                    {
                      title: '净增长',
                      key: 'net_growth',
                      render: (_: unknown, record: MonthlyGrowth) => {
                        const net = record.new_count - record.churn_count;
                        return (
                          <span style={{ color: net >= 0 ? COLORS.success : COLORS.error }}>
                            {net >= 0 ? '+' : ''}{net}
                          </span>
                        );
                      },
                    },
                    { title: '累计活跃', dataIndex: 'total_active', key: 'total_active' },
                  ]}
                  dataSource={growthTrend}
                  rowKey="month"
                  size="small"
                  pagination={false}
                />
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </Spin>
  );
};

export default AdminDashboard;
